"use client";

import { type MouseEvent, useEffect, useId, useRef } from "react";
import { Button } from "../../../elements/button/button";
import { cn } from "../../../lib/utils";
import { mobileNavigationBarClasses } from "../main-navigation.constants";
import type { MainNavigationAction } from "../main-navigation.types";

export interface NavigationActionsProps {
  actions: MainNavigationAction[];
  openActionId?: string;
  onToggle: (actionId: string) => void;
  onClose: () => void;
  /**
   * Desktop panels drop below the header bar and are positioned against it, so
   * no element between the bar and this component may be positioned. Mobile
   * panels cover the page above the mobile navigation bar.
   */
  placement: "desktop" | "mobile";
}

const ACTION_ATTRIBUTE = "data-navigation-action";
// Larger counts show as "99+" so the pill stays within the icon button.
const MAX_VISIBLE_COUNT = 99;

const PANEL_CLASSES: Record<NavigationActionsProps["placement"], string> = {
  // The transparent top padding bridges the gap below the header bar, the
  // same way the mega menus do.
  desktop: cn(
    "absolute top-full right-0 z-10 w-1/2 min-w-xl pt-4 outline-none",
    "starting:-translate-y-3 translate-y-0 opacity-100 starting:opacity-0",
    "transition-[opacity,translate] duration-200 ease-out motion-reduce:transition-none"
  ),
  // Ends above the navigation bar: inside the bar's stacking context it would
  // otherwise cover the bar's own buttons.
  mobile: cn(
    "fixed inset-x-0 top-[var(--site-preview-offset,0px)] z-40 overflow-y-auto bg-white px-3 pt-15 pb-6 outline-none motion-safe:animate-nav-panel-in lg:hidden",
    mobileNavigationBarClasses.panelBottom
  ),
};

/**
 * Icon buttons of the header bar. Actions with a panel toggle it instead of
 * following a link; the panel is a non-modal dialog that takes focus when it
 * opens and hands it back to its button on Escape.
 */
export function NavigationActions(props: Readonly<NavigationActionsProps>) {
  const idPrefix = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { onClose, placement } = props;
  const openAction = props.actions.find(
    (action) => action.id === props.openActionId && action.panel
  );
  const openActionId = openAction?.id;
  const panelId = `${idPrefix}-panel`;

  // Search and login start with their field, even when a button comes first.
  // Panels without a field, e.g. the cart confirmation, focus the dialog
  // itself, so that it is announced and Tab continues inside it.
  useEffect(() => {
    const panel = panelRef.current;
    if (openActionId && panel) {
      (
        panel.querySelector<HTMLElement>("input:not([disabled])") ?? panel
      ).focus();
    }
  }, [openActionId]);

  useEffect(() => {
    if (!openActionId) {
      return;
    }

    const actionId = openActionId;
    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== "Escape") {
        return;
      }
      const focused = document.activeElement;
      if (
        panelRef.current?.contains(focused) ||
        listRef.current?.contains(focused)
      ) {
        findTrigger(listRef.current, actionId)?.focus();
      }
      onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [openActionId, onClose]);

  // A mobile panel covers the page, so the page behind it must not scroll along.
  // When the app drives both bars with one state, the mobile panel is also
  // open on desktop, where it is hidden and must leave the page alone.
  useEffect(() => {
    if (
      !openActionId ||
      placement !== "mobile" ||
      panelRef.current?.checkVisibility?.() === false
    ) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [openActionId, placement]);

  // Client side navigation keeps the header mounted, so links close the panel.
  function handlePanelClick(event: MouseEvent<HTMLDivElement>) {
    if ((event.target as Element).closest("a[href]")) {
      onClose();
    }
  }

  return (
    <>
      <ul className="flex items-center gap-1" ref={listRef}>
        {props.actions.map((action) => {
          const isOpen = action.id === openActionId;
          // The design shows the open state as a filled circle.
          const variant = isOpen ? "secondary" : "ghost";
          const count = getVisibleCount(action.count);
          // The same wording as the current header: "Cart (3)".
          const label =
            count === undefined ? action.label : `${action.label} (${count})`;
          const content = (
            <>
              {action.icon}
              {count === undefined ? null : <ActionCount count={count} />}
            </>
          );

          if (action.href) {
            return (
              <li key={action.id}>
                <Button
                  aria-label={label}
                  className="relative rounded-full"
                  data-navigation-action={action.id}
                  href={action.href}
                  mode="D2C"
                  size="icon"
                  variant={variant}
                >
                  {content}
                </Button>
              </li>
            );
          }

          return action.panel ? (
            <li key={action.id}>
              <Button
                aria-controls={isOpen ? panelId : undefined}
                aria-expanded={isOpen}
                aria-label={label}
                className="relative rounded-full"
                data-navigation-action={action.id}
                mode="D2C"
                onClick={() => props.onToggle(action.id)}
                size="icon"
                type="button"
                variant={variant}
              >
                {content}
              </Button>
            </li>
          ) : null;
        })}
      </ul>

      {openAction?.panel ? (
        <div
          aria-label={openAction.label}
          className={PANEL_CLASSES[placement]}
          data-testid={`navigation-action-panel-${placement}`}
          id={panelId}
          onClick={handlePanelClick}
          ref={panelRef}
          role="dialog"
          tabIndex={-1}
        >
          {placement === "desktop" ? (
            <div className="max-h-[calc(100dvh-5.5rem)] overflow-y-auto rounded-lg bg-white p-8">
              {openAction.panel(onClose)}
            </div>
          ) : (
            openAction.panel(onClose)
          )}
        </div>
      ) : null}
    </>
  );
}

function getVisibleCount(count: number | null | undefined): number | undefined {
  return typeof count === "number" && count > 0 ? count : undefined;
}

/**
 * Pill in the style of the D2C badges, on the top right of the icon. The white
 * ring keeps it apart from the icon's strokes. The button's accessible name
 * carries the count, so the pill itself is hidden from screen readers.
 */
function ActionCount(props: Readonly<{ count: number }>) {
  return (
    <span
      aria-hidden="true"
      className="absolute top-0.5 right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-black px-1 font-bold font-brand text-[10px] text-white tabular-nums leading-none ring-2 ring-white"
      data-testid="navigation-action-count"
    >
      {props.count > MAX_VISIBLE_COUNT ? `${MAX_VISIBLE_COUNT}+` : props.count}
    </span>
  );
}

function findTrigger(
  list: HTMLElement | null,
  actionId: string
): HTMLElement | undefined {
  return Array.from(
    list?.querySelectorAll<HTMLElement>(`[${ACTION_ATTRIBUTE}]`) ?? []
  ).find((element) => element.getAttribute(ACTION_ATTRIBUTE) === actionId);
}
